import { Plugin, PluginKey } from 'prosemirror-state'

import Styles from './TableRightClickMenu.module.scss'
import TableMenuContent from './TableRightClickMenuContent'
import { type EditorView } from 'prosemirror-view'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { cellUnderMouse } from '../tableSelection/tableSelectionUtils'
import { isCellSelectionIncluding, isTableSelectionIncluding } from 'common/Prose/utils/table'
import { createRoot, Root } from 'react-dom/client'

const defaultState = {
  active: false,
  xPos: 0,
  yPos: 0,
}

export const tablePluginKey = new PluginKey('tableMenu')

const MENU_HEIGHT_ADJUST = -312

function handleMouseDown(view: EditorView, event: Event) {
  const mouseEvent = event as MouseEvent
  if (mouseEvent.button === 2) {
    handleRightClick(view, mouseEvent)
  }
  return false
}

const handleRightClick = (view: EditorView, mouseEvent: MouseEvent) => {
  closeContextMenu(view)
  const selection = view.state.selection
  let $head = cellUnderMouse(view, mouseEvent)
  if ($head) {
    mouseEvent.preventDefault()
    mouseEvent.stopImmediatePropagation()
    if (isCellSelectionIncluding($head, selection) || isTableSelectionIncluding($head, selection)) {
      return true
    }
    let newSelection = new CellSelection($head, $head)
    let tr = view.state.tr.setSelection(newSelection)
    view.dispatch(tr)
    return true
  }
}

const targetInSameTable = (target: HTMLElement, view: EditorView) => {
  if (!(view.state.selection instanceof CellSelection)) return false
  const maybeTable = target.closest('[data-node-type="table"]')
  if (!maybeTable) return false
  const posBeforeTable = view.posAtDOM(maybeTable, 0) - 1
  const tableOfSelection = view.state.selection.$anchorCell.node(-1)
  return view.state.doc.nodeAt(posBeforeTable) === tableOfSelection
}

function handleContextMenu(view: EditorView, event: Event) {
  if (!view.editable) return false

  const mouseEvent = event as MouseEvent
  const eventTarget = mouseEvent.target
  if (!eventTarget) return false
  if (
    mouseEvent.button === 2 &&
    (cellUnderMouse(view, mouseEvent) || targetInSameTable(eventTarget as HTMLElement, view))
  ) {
    event.preventDefault()
    const tx = view.state.tr.setMeta('openTableMenu', true)
    if (mouseEvent.clientY > window.innerHeight / 2) {
      // Open menu upwards to prevent going off screen
      tx.setMeta('yPos', mouseEvent.clientY + MENU_HEIGHT_ADJUST)
    } else {
      tx.setMeta('yPos', mouseEvent.clientY)
    }
    tx.setMeta('xPos', mouseEvent.clientX)
    view.dispatch(tx)
    return true
  }
  return false
}

function closeContextMenu(view: EditorView) {
  const tx = view.state.tr.setMeta('closeTableMenu', true)
  view.dispatch(tx)
}

export const TableRightClickMenu = () => {
  return new Plugin({
    key: tablePluginKey,
    props: {
      handleDOMEvents: {
        mousedown: (view, event) => {
          return handleMouseDown(view, event)
        },
        contextmenu: (view, event) => {
          return handleContextMenu(view, event)
        },
      },

      createSelectionBetween(view) {
        const pluginState = tablePluginKey.getState(view.state)
        if (pluginState && pluginState.active) {
          return view.state.selection
        }
        return undefined
      },
    },
    view(editorView) {
      let containerDom: HTMLDivElement
      let root: Root

      const clickOutside = (e: MouseEvent) => {
        if (!(e.target instanceof Node) || !containerDom.contains(e.target)) {
          closeContextMenu(editorView)
        }
      }

      return {
        update: (view, prevState) => {
          const prevPluginState = tablePluginKey.getState(prevState)
          const pluginState = tablePluginKey.getState(view.state)

          if (prevPluginState.active && pluginState.active && containerDom && root) {
            root.render(
              <TableMenuContent
                view={view}
                position={{ x: pluginState.xPos, y: pluginState.yPos }}
                closeMenu={closeContextMenu}
              />,
            )
          }

          if (!prevPluginState.active && pluginState.active) {
            setTimeout(() => window.addEventListener('click', clickOutside), 50)
            containerDom = document.createElement('div')
            root = createRoot(containerDom)
            containerDom.className = Styles.positionContainer
            containerDom.style.left = pluginState.xPos + 'px'
            containerDom.style.top = pluginState.yPos + 'px'
            root.render(
              <TableMenuContent
                view={view}
                position={{ x: pluginState.xPos, y: pluginState.yPos }}
                closeMenu={closeContextMenu}
              />,
            )

            if (view.dom.parentNode) {
              view.dom.parentNode.appendChild(containerDom)
            }
          }

          if (prevPluginState.active && !pluginState.active) {
            window.removeEventListener('click', clickOutside)
            root.unmount()
            containerDom.remove()
          }
        },
      }
    },
    state: {
      init() {
        return defaultState
      },

      apply(tr, prevState, old, updated) {
        const next = { ...prevState }
        const openFlag = tr.getMeta('openTableMenu')
        const closeFlag = tr.getMeta('closeTableMenu')

        if (closeFlag) {
          return defaultState
        }

        if (openFlag) {
          const xPos = tr.getMeta('xPos')
          const yPos = tr.getMeta('yPos')
          next.xPos = xPos
          next.yPos = yPos
          next.active = true
        }
        return next
      },
    },
  })
}
